import { Controller } from "@hotwired/stimulus"

// Accessible tab group. Reads/writes the URL hash so deep links to a specific
// tab survive refresh. Arrow keys move focus between tabs. Enter/Space and
// click activate a tab.
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { defaultTab: String }

  connect() {
    this.activateFromHash()
    window.addEventListener("hashchange", this.activateFromHash.bind(this))
  }

  disconnect() {
    window.removeEventListener("hashchange", this.activateFromHash.bind(this))
  }

  activateFromHash() {
    const hashTab = (window.location.hash || "").replace(/^#/, "")
    const fallback = this.defaultTabValue || (this.tabTargets[0]?.dataset.tab ?? "")
    const target = this.hasTabByName(hashTab) ? hashTab : fallback
    if (target) this.activate(target, { updateHash: false })
  }

  hasTabByName(name) {
    return !!name && this.tabTargets.some((t) => t.dataset.tab === name)
  }

  select(event) {
    event.preventDefault()
    const name = event.currentTarget.dataset.tab
    this.activate(name, { updateHash: true })
  }

  onKeydown(event) {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"]
    if (!keys.includes(event.key)) return
    event.preventDefault()
    const tabs = this.tabTargets
    const current = tabs.indexOf(event.currentTarget)
    let next = current
    if (event.key === "ArrowRight") next = (current + 1) % tabs.length
    if (event.key === "ArrowLeft")  next = (current - 1 + tabs.length) % tabs.length
    if (event.key === "Home")       next = 0
    if (event.key === "End")        next = tabs.length - 1
    tabs[next].focus()
    this.activate(tabs[next].dataset.tab, { updateHash: true })
  }

  activate(name, { updateHash } = { updateHash: true }) {
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.tab === name
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.setAttribute("tabindex", active ? "0" : "-1")
      tab.classList.toggle("is-active", active)
    })
    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.tab === name
      panel.hidden = !active
      panel.setAttribute("aria-hidden", active ? "false" : "true")
    })
    if (updateHash && name) {
      const url = new URL(window.location.href)
      url.hash = name
      history.replaceState(null, "", url.toString())
    }
  }
}
